<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TableImage from "@/components/shared/TableImage.vue"
import TablePaginator from "@/components/shared/TablePaginator.vue"
import { getLocaleMessages } from "@/locales/index"
import type { ListRequest } from "@/services/quality"
import qualityService from "@/services/quality"
import type { Option } from "@/types/common.type"
import type { Quality } from "@/types/quality.type"
import { reportSentry } from "@/utils/sentry"
import { normalizeString } from "@/utils/string"

const route = useRoute()
const router = useRouter()

const items = ref<Quality[]>([])
const total = ref(0)

const query = ref<ListRequest>({
  page: (route.query.page as string) || "1",
  perPage: (route.query.perPage as string) || "25",
  name: (route.query.name as string) || "",
  status: (route.query.status as string) || "",
})

const statusOptions = computed(() => {
  const options = [] as Option[]
  const statusList = getLocaleMessages().item.statusList
  for (const key in statusList) options.push({ value: key, label: statusList[key as keyof typeof statusList] })
  return options
})

// fetch function
async function loadItems() {
  try {
    const params = Object.entries(query.value).reduce((acc, [key, value]) => {
      acc[key as keyof ListRequest] = value === "" ? null : value
      return acc
    }, {} as ListRequest)

    const data = await qualityService.list(params)
    items.value = data.items
    total.value = data.total
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadItems()
})

// re-load whenever the query string changes
watch(
  () => route.query,
  (newQuery) => {
    query.value = {
      page: (newQuery.page as string) || "1",
      perPage: (newQuery.perPage as string) || "25",
      name: (newQuery.name as string) || "",
      status: (newQuery.status as string) || "",
    }

    loadItems()
  },
)

// update URL (and trigger the watch) when the form submits
function searchItems() {
  query.value.page = "1"
  const params = Object.fromEntries(Object.entries(query.value).filter(([_, v]) => v !== ""))

  router.push({ path: route.path, query: params })
}
</script>

<template>
  <LayoutAuthed :title="$t('nav.items')">
    <Card>
      <CardTitle>{{ $t("shared.search") }}</CardTitle>
      <form @submit.prevent="searchItems()">
        <CardContent>
          <div class="row">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox v-model="query.name" :label="$t('item.name')" type="text" field="name" />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputSelect v-model="query.status" :label="$t('item.status')" field="status" :options="statusOptions" />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button type="submit" :text="$t('shared.search')" />
        </CardActions>
      </form>
    </Card>
    <Card>
      <CardTitle>
        <template #default> {{ $t("shared.results") }} ({{ total }}) </template>
        <template #actions>
          <Button type="link" path="/items/new" :text="$t('item.new')" color="danger" size="sm" />
        </template>
      </CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("item.img") }}</th>
              <th>{{ $t("item.name") }}</th>
              <th>{{ $t("item.isPrintOrderable") }}</th>
              <th>{{ $t("item.category") }}</th>
              <th>{{ $t("item.contents") }}</th>
              <th>{{ $t("item.status") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(item, index) in items" :key="index">
              <td>
                <TableImage v-if="item.storeSwatchImage" :image="item.storeSwatchImage" />
                <span v-else>-</span>
              </td>
              <td>
                <router-link :to="`/items/${item.id}`">{{ item.name }}</router-link>
              </td>
              <td>{{ $t(`shared.boolean.${item.isPrintOrderable}`) }}</td>
              <td>{{ normalizeString(item.category?.name) }}</td>
              <td>{{ normalizeString(item.contents.map((c) => `${c.abbreviation} ${c.ratio}`).join(", ")) }}</td>
              <td>
                <StatusBadge model="item" field="status" :value="item.status" />
              </td>
            </tr>
          </template>
        </Table>
        <TablePaginator
          :total="total"
          :perPage="Number(query.perPage)"
          :page="Number(query.page)"
          :url="route.fullPath"
        />
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped></style>
